import { useCart } from 'components/Cart';
import { salesTaxQuotation } from 'components/Checkout/checkoutHelpers';
import {
  CheckoutStep,
  useCheckoutState,
} from 'components/CheckoutML/CheckoutStateProvider';
import { useEffect } from 'react';
import { PRODUCT_SLUGS, QUEST_SLUGS } from 'utils/constants/products';
import { logError } from 'utils/helpers';
import { Order } from 'utils/types';

export type CheckoutQuestStateType = {
  details: CheckoutStep<{
    firstName: string;
    lastName: string;
    email: string;
    password: string;
    address1: string;
    address2: string;
    city: string;
    state: string;
    zipCode: string;
    dob: string;
    sex: 'male' | 'female' | '';
    phone: string;
    wantsEmail: boolean;
    wantsSms: boolean;
  }>;
  medical?: CheckoutStep<{
    isCurrentlyPregnant?: 'yes' | 'no';
    hasHadHysterectomy?:
      | 'yes_removal_both_ovaries'
      | 'yes_removal_one_ovary'
      | 'yes_no_removal'
      | 'no';
    pregnancyWeeks?: number;
    hasHadMenstrualPeriodInPastYear?:
      | 'yes'
      | 'no_regular_periods'
      | 'no_periods_over_12_months';
    lastMenstrualPeriodDate?: string;
    menstrualCycleDurationDays?: number;
  }>;
};

export const checkoutQuestInitialState: CheckoutQuestStateType = {
  details: {
    data: {
      firstName: '',
      lastName: '',
      email: '',
      password: '',
      address1: '',
      address2: '',
      city: '',
      state: '',
      zipCode: '',
      dob: '',
      sex: '',
      phone: '',
      wantsEmail: true,
      wantsSms: false,
    },
  },
};

/**
 * Hook to get the checkout state for the Quest checkout.
 * It helps to get the proper type for the checkout state.
 * @returns The checkout state for the Quest checkout.
 */

export const useCheckoutQuestContext = () =>
  useCheckoutState<CheckoutQuestStateType>();

const isProductInOrder = (order: Order, productSkus: string[]) =>
  order.line_items.some((item) => productSkus.includes(item.product_sku ?? ''));

export const isQuest360Female = (order: Order) =>
  isProductInOrder(order, [PRODUCT_SLUGS.EVERLYWELL_360_FEMALE]);

export const isQuest360 = (order: Order) =>
  isProductInOrder(order, [
    PRODUCT_SLUGS.EVERLYWELL_360_FEMALE,
    PRODUCT_SLUGS.EVERLYWELL_360_MALE,
  ]);

export const isQuestGalleri = (order: Order) =>
  isProductInOrder(order, [PRODUCT_SLUGS.GALLERI_IN_PERSON_TEST]);

export const isGalleri = (slug: string) =>
  slug === PRODUCT_SLUGS.GALLERI_IN_PERSON_TEST;

/**
 * Custom hook to handle tax calculation
 * for the Quest checkout.
 */
export const useQuestTaxCalculation = () => {
  const { order } = useCart();
  const { state, setTaxQuote } = useCheckoutQuestContext();
  const zipCode = state.details?.data?.zipCode;

  useEffect(() => {
    const calculateTax = async () => {
      // Reset tax quote if zipCode is not available
      if (!zipCode) {
        setTaxQuote(undefined);
        return;
      }

      if (!order?.id) {
        return;
      }

      try {
        const quote = await salesTaxQuotation(order.id, { zipcode: zipCode });
        setTaxQuote(quote.data?.total_tax);
      } catch (error: any) {
        logError('Error getting sales tax quote', error);
        setTaxQuote(undefined);
      }
    };

    calculateTax();
  }, [zipCode, order, setTaxQuote]);
};
export const isAnyQuestProductInOrder = (order: Order) =>
  isProductInOrder(order, QUEST_SLUGS);
